import http from 'services/http';

export type MappingType = 'groups' | 'plan_types' | 'group_types' | 'episodes' | 'locations';
export const MAPPING_TYPES: readonly string[] = ['groups', 'plan_types', 'group_types', 'episodes', 'locations'];
export const isMappingType = (name: string | null | undefined): name is MappingType =>
  !!name && MAPPING_TYPES.includes(name);
export type MappingTarget = string | string[];

export const DONT_MAP = '__dont_map__';
export const WILDCARD = '*';
export const INSTRUCTIONS_LIMIT = 4000;
export const OPERATIONS_LIMIT = 500;

export type MappingRecord = { id: string; name: string };

export type MappingEntry = {
  key: string;
  target: MappingTarget;
  dontMap: boolean;
  resolved: MappingRecord | null;
  setBy: 'person' | 'agent' | null;
  actorLabel: string | null;
  actorId: string | null;
  setAt: string | null;
  confidence: number | null;
  needsReview: boolean;
  previousTarget: MappingTarget | null;
  agentTarget: MappingTarget | null;
  agentConfidence: number | null;
};

export type AgentMode = 'suggest' | 'auto';

export type AgentSettings = { enabled: boolean; mode: AgentMode; threshold: number };

export type MappingsResponse = {
  fallback: MappingTarget | null;
  instructions: string | null;
  needsReviewCount: number;
  agent: AgentSettings;
  networkAddable: boolean;
  data: MappingEntry[];
};

export type ReceivedValue = {
  id: string;
  name: string;
  firstSeenAt: string;
  lastSeenAt: string | null;
  recordCount: number;
  dismissed: boolean;
  dismissedAt: string | null;
  status: 'unmapped' | 'suggested' | 'mapped' | 'dont_map' | 'dismissed';
  rule: string | null;
  target: MappingRecord | null;
  needsReview: boolean;
  suggestion: {
    target: MappingRecord | null;
    agentTarget: MappingTarget | null;
    confidence: number | null;
    suggestedAt: string | null;
    inNetwork: boolean | null;
  } | null;
};

export type ReceivedValueStatus = 'all' | 'new';

export type MappingImpact = {
  selfHeal: boolean;
  affected: {
    totalAttributed: number;
    wouldUpdate: number;
    leftAloneOther: number;
    leftAloneNotApplicable: number;
    alreadyCurrent: number;
  } | null;
};

export type MappingAttention = { type: MappingType; needsReview: number; unmapped: number };

type Keyed = { type: MappingType };

export type MappingOperation =
  | (Keyed & { op: 'set'; key: string; target: MappingTarget; expected: MappingTarget | null; addToNetwork?: boolean })
  | (Keyed & { op: 'acknowledge' | 'dont_map' | 'remove'; key: string; expected: MappingTarget | null })
  | (Keyed & { op: 'dismiss'; key: string; attrValueId: string })
  | (Keyed & { op: 'accept_suggestion'; key: string; expected: MappingTarget | null; addToNetwork?: boolean })
  | (Keyed & { op: 'reject_suggestion'; key: string })
  | (Keyed & { op: 'settings'; threshold: number })
  | (Keyed & { op: 'instructions'; text: string })
  | (Keyed & { op: 'fallback'; target: string[] | null; expected: MappingTarget | null });

export type MappingOperationResult = {
  index: number;
  op: MappingOperation['op'];
  type: MappingType;
  key: string | null;
  status: 'ok' | 'conflict' | 'invalid' | 'forbidden';
  code?: string | null;
  message?: string | null;
  current?: MappingTarget | null;
  currentLabel?: string | null;
  details?: unknown;
};

export type ImportConfigurationSaveResult = {
  mappingChanges: (MappingOperationResult & { affected?: unknown })[];
  selfHealRecords: number;
};

export const adminMappingsQueryKeys = {
  all: (configId: string) => ['adminMappings', configId] as const,
  entries: (configId: string, type: MappingType) => ['adminMappings', configId, type, 'entries'] as const,
  receivedValues: (configId: string, type: MappingType, status: ReceivedValueStatus) =>
    ['adminMappings', configId, type, 'receivedValues', status] as const,
  impact: (configId: string, type: MappingType, key: string, to?: string) =>
    ['adminMappings', configId, type, 'impact', key, to ?? null] as const,
  attention: (configId: string) => ['adminMappings', configId, 'attention'] as const,
  attentionTotal: ['adminMappingsAttentionTotal'] as const,
};

export const ADMIN_MAPPING_TARGETS_QUERY_KEY = ['adminMappingTargets'] as const;

const base = (configId: string) => `admin/import_configurations/${configId}`;

export async function adminIndexMappings(configId: string, type: MappingType) {
  return http.get<MappingsResponse>(`${base(configId)}/mappings`, { params: { type } }).then((res) => res.data);
}

export async function adminIndexReceivedValues(configId: string, type: MappingType, status: ReceivedValueStatus) {
  return http
    .get<{ data: ReceivedValue[] }>(`${base(configId)}/received_values`, { params: { type, status } })
    .then((res) => res.data.data);
}

export async function adminMappingImpact(configId: string, type: MappingType, key: string, to?: string) {
  return http
    .get<MappingImpact>(`${base(configId)}/mappings/${type}/impact`, { params: { key, to } })
    .then((res) => res.data);
}

export async function adminMappingAttention(configId: string) {
  return http.get<{ data: MappingAttention[] }>(`${base(configId)}/mappings/attention`).then((res) => res.data.data);
}

export async function adminMappingAttentionTotal() {
  return http.get<{ count: number }>('admin/import_configurations/needs_attention_count').then((res) => res.data.count);
}

export async function adminSaveImportConfiguration(
  configId: string,
  payload: Record<string, unknown> & { mappingOperations: MappingOperation[]; configDigest?: string }
) {
  return http.put<ImportConfigurationSaveResult>(`${base(configId)}`, payload).then((res) => res.data);
}

export function mappingOperationResults(error: unknown): MappingOperationResult[] | null {
  const data = (error as { response?: { data?: { mappingResults?: MappingOperationResult[] } } })?.response?.data;
  return data?.mappingResults ?? null;
}
